/**
 * Панель «+ / −» під вибором варіації на сторінці товару.
 *
 * У main.js цих селекторів немає взагалі (`.panel-add`, `.panel-subtract`,
 * `.number-quantify` — нуль збігів), тобто верстка віддала саму розмітку без
 * поведінки. Тому модуль тут, а не патч до збілдженого файла (§7.5 CLAUDE.md).
 *
 * Клас `.is-disabled` не вигаданий: main.scss уже описує його для `.panel-btn`
 * (cursor:not-allowed, opacity .2), тобто дизайн передбачав межі — лишалось їх
 * увімкнути.
 *
 * Значення живе у двох місцях і мусить збігатися:
 *
 *   `.number-quantify` — те, що бачить людина. Двоцифрове, з нулем попереду
 *       («01»), як у _design/dist/publication.html;
 *   `input[name=quantity]` — те, що піде в WooCommerce. Ховане поле, бо
 *       додавати ще один видимий інпут верстка не передбачає, а
 *       WC_Form_Handler читає саме 'quantity'.
 *
 * Верхня межа — `data-max` на панелі, і вона є лише тоді, коли товару ведуть
 * облік кількості. Без неї «+» не впирається ні в що: непроданої межі просто
 * немає, і вигадувати її на клієнті не можна — сервер усе одно перевірить.
 *
 * ⚠️ Імена класів у верстці інвертовані щодо гліфів. Базова маска
 * `.panel-btn:before` — мінус, і `.panel-subtract:before` перевизначає її на
 * `--vecAccordPlusB`. Тобто `.panel-add` показує «−», а `.panel-subtract` —
 * «+», і саме так вони тут і працюють. Класи не перейменовуємо: на них
 * зав'язаний main.scss, а він збілджений (§7.5 CLAUDE.md).
 */

const MIN = 1;

/**
 * Двоцифровий підпис: 1 -> «01», 12 -> «12».
 *
 * padStart, а не ручна конкатенація: на 100+ рядок мусить лишитись «100», а
 * не «0100».
 */
const label = (value) => String(value).padStart(2, '0');

const setDisabled = (button, disabled) => {
  if (!button) return;

  button.classList.toggle('is-disabled', disabled);
  // aria-disabled, а не disabled: вимкнена кнопка випадає з таб-порядку, і
  // фокус із неї стрибає в непередбачуване місце просто тому, що дійшли до
  // межі. Клік однаково гаситься перевіркою нижче.
  button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
};

const initPanel = (panel) => {
  const output = panel.querySelector('.number-quantify');
  const input = panel.querySelector('input[name="quantity"]');
  // Іменуємо змінні за ДІЄЮ, а не за класом: «−» лежить на .panel-add, «+» —
  // на .panel-subtract (див. шапку файла).
  const minusBtn = panel.querySelector('.panel-add');
  const plusBtn = panel.querySelector('.panel-subtract');

  if (!output || !input) return;

  // Стеля читається щоразу заново, а не один раз: у варіативного товару вона
  // залежить від обраної варіації, і helpers/variation-price.js переписує
  // 'data-max' на льоту, повідомляючи про це подією.
  const readMax = () => {
    const parsed = parseInt(panel.dataset.max, 10);

    return Number.isFinite(parsed) && parsed >= MIN ? parsed : Infinity;
  };

  let max = readMax();
  let value = MIN;

  const render = () => {
    output.textContent = label(value);
    input.value = String(value);

    setDisabled(minusBtn, value <= MIN);
    setDisabled(plusBtn, value >= max);
  };

  const step = (delta) => {
    const next = Math.min(max, Math.max(MIN, value + delta));

    if (next === value) return;

    value = next;
    render();
  };

  plusBtn?.addEventListener('click', () => step(1));
  minusBtn?.addEventListener('click', () => step(-1));

  // Змінилась обрана варіація — стеля інша. Поточне значення підтягуємо вниз,
  // якщо воно вже не влазить: інакше у формі лишилась би кількість, якої
  // немає в наявності, і замовлення відхилив би вже сервер.
  panel.addEventListener('nmhg:max-changed', () => {
    max = readMax();

    if (value > max) value = max;

    render();
  });

  render();
};

const init = () => document.querySelectorAll('[data-quantity]').forEach(initPanel);

// Той самий хук, що й у сусідніх хелперів. Модулі Vite і так виконуються
// відкладено, тобто DOM уже розібраний, але покладатись на це означало б
// зав'язатись на спосіб підключення, а не на стан документа.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
